import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Mail, Phone, MapPin, Package, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [selectedServices, setSelectedServices] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');

  useEffect(() => {
    fetchVendor();
  }, [vendorId]);

  const fetchVendor = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vendor/${vendorId}`);
      setVendor(response.data);
      
      // Initialize services with 0 quantity
      const initialServices = response.data.services.map(service => ({
        ...service,
        quantity: 0
      }));
      setSelectedServices(initialServices);
    } catch (error) {
      console.error('Error fetching vendor:', error);
      toast.error('Failed to load vendor information');
      navigate('/vendors');
    } finally {
      setLoading(false);
    }
  };

  const updateServiceQuantity = (serviceId, quantity) => {
    setSelectedServices(prev =>
      prev.map(service =>
        service.id === serviceId
          ? { ...service, quantity: Math.max(0, quantity) }
          : service
      )
    );
  };

  const calculateTotal = () => {
    return selectedServices.reduce((total, service) => {
      return total + (service.quantity * service.price_per_lb);
    }, 0);
  };

  const getSelectedServicesForBooking = () => {
    return selectedServices
      .filter(service => service.quantity > 0)
      .map(service => ({
        service_id: service.id,
        service_name: service.name,
        quantity_lbs: service.quantity,
        price_per_lb: service.price_per_lb,
        total_price: service.quantity * service.price_per_lb
      }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const servicesForBooking = getSelectedServicesForBooking();
    if (servicesForBooking.length === 0) {
      toast.error('Please select at least one service');
      return;
    }

    if (!pickupDate || !pickupTime) {
      toast.error('Please select pickup date and time');
      return;
    }

    try {
      setSubmitting(true);
      
      const pickupDateTime = new Date(`${pickupDate}T${pickupTime}`);
      let deliveryDateTime = null;
      
      if (deliveryDate && deliveryTime) {
        deliveryDateTime = new Date(`${deliveryDate}T${deliveryTime}`);
      }

      const bookingData = {
        customer: customerInfo,
        vendor_id: vendorId,
        services: servicesForBooking,
        pickup_date: pickupDateTime.toISOString(),
        delivery_date: deliveryDateTime ? deliveryDateTime.toISOString() : null,
        total_amount: calculateTotal(),
        special_instructions: specialInstructions || null
      };

      const response = await axios.post(`${API}/booking`, bookingData);
      toast.success('Booking created successfully!');
      navigate(`/confirmation/${response.data.id}`);
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getServiceTypeDisplay = (serviceType) => {
    const types = {
      'wash_and_fold': 'Wash & Fold',
      'dry_clean': 'Dry Clean',
      'ironing': 'Ironing',
      'stain_removal': 'Stain Removal'
    };
    return types[serviceType] || serviceType;
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking information...</p>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Vendor not found</p>
          <Link to="/vendors" className="btn-primary">
            Back to Vendors
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/vendors" className="text-teal-600 hover:text-teal-700 transition-colors">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Book Service</h1>
              <p className="text-gray-600">{vendor.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Vendor Info & Services */}
          <div className="space-y-6">
            {/* Vendor Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="text-teal-600" size={20} />
                  Vendor Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-teal-600" />
                    <span className="text-sm">{vendor.address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-teal-600" />
                    <span className="text-sm">{vendor.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-teal-600" />
                    <span className="text-sm">{vendor.operating_hours}</span>
                  </div>
                  {vendor.website && (
                    <div className="flex items-center gap-2">
                      <Globe size={16} className="text-teal-600" />
                      <a 
                        href={vendor.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-teal-600 hover:text-teal-700 underline"
                      >
                        Visit Website
                      </a>
                    </div>
                  )}
                </div>
                <p className="text-gray-600 mt-4">{vendor.description}</p>
              </CardContent>
            </Card>

            {/* Services Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Select Services</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {selectedServices.map((service) => (
                    <div key={service.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold">{getServiceTypeDisplay(service.type)}</h4>
                          <p className="text-sm text-gray-600">{service.description}</p>
                          <p className="text-sm font-medium text-teal-600">${service.price_per_lb}/lb</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-3">
                        <Label htmlFor={`quantity-${service.id}`} className="text-sm">
                          Quantity (lbs):
                        </Label>
                        <Input
                          id={`quantity-${service.id}`}
                          type="number"
                          min="0"
                          step="0.5"
                          value={service.quantity}
                          onChange={(e) => updateServiceQuantity(service.id, parseFloat(e.target.value) || 0)}
                          className="w-20"
                        />
                        {service.quantity > 0 && (
                          <span className="text-sm font-medium text-teal-600">
                            = ${(service.quantity * service.price_per_lb).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Booking Form */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="text-teal-600" size={20} />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        required
                        value={customerInfo.name}
                        onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter your name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        required
                        value={customerInfo.phone}
                        onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="Enter your phone"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="address">Pickup Address *</Label>
                    <Input
                      id="address"
                      required
                      value={customerInfo.address}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Enter your address"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pickup-date">Pickup Date *</Label>
                      <Input
                        id="pickup-date"
                        type="date"
                        required
                        min={getMinDate()}
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="pickup-time">Pickup Time *</Label>
                      <Input
                        id="pickup-time"
                        type="time"
                        required
                        value={pickupTime}
                        onChange={(e) => setPickupTime(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="delivery-date">Delivery Date (Optional)</Label>
                      <Input
                        id="delivery-date"
                        type="date"
                        min={pickupDate || getMinDate()}
                        value={deliveryDate}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="delivery-time">Delivery Time</Label>
                      <Input
                        id="delivery-time"
                        type="time"
                        value={deliveryTime}
                        onChange={(e) => setDeliveryTime(e.target.value)}
                        disabled={!deliveryDate}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="instructions">Special Instructions</Label>
                    <Textarea
                      id="instructions"
                      value={specialInstructions}
                      onChange={(e) => setSpecialInstructions(e.target.value)}
                      placeholder="Any special instructions for handling your items..."
                      rows={3}
                    />
                  </div>

                  {/* Order Summary */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Order Summary</h4>
                    {getSelectedServicesForBooking().map((service) => (
                      <div key={service.service_id} className="flex justify-between text-sm mb-2">
                        <span>{service.service_name} ({service.quantity_lbs} lbs)</span>
                        <span>${service.total_price.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t pt-2 flex justify-between font-semibold">
                      <span>Total:</span>
                      <span className="text-teal-600">${calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    disabled={submitting || calculateTotal() === 0}
                  >
                    {submitting ? (
                      <>
                        <div className="loading-spinner mr-2"></div>
                        Creating Booking...
                      </>
                    ) : (
                      'Book Service'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default BookingPage;